import React, { useState } from "react";
import { money, num } from "./doorMarginFormat";
import { fmtQty, pluralize, unitNoun } from "../../../../utils/inventoryContents";

/**
 * Itemized view of one snapshot: per-kind materials lines (Recipe = qty × price;
 * Actual = observed vs predicted with the calibration scale and its flags) and
 * the overhead lines (software w/ incomplete flag, marketing blended + per
 * source with the doors it covered, damage, other). Everything here is read
 * straight off the stored snapshot; nothing is recomputed on the client.
 * Per-door quantities also read in pieces for items with contents set
 * (snap.itemContents = TODAY'S contents; the stock-unit number is the stored
 * truth and is shown beside it).
 */
const DoorMarginBreakdown = ({ snap, mode }) => {
  const [kind, setKind] = useState("refinish");
  const [open, setOpen] = useState(true);
  const block = snap.perKind?.[kind]?.[mode];
  const lines = block?.lines || [];
  const ovh = snap.overheadPerDoor || {};
  const allDoors = Number(snap.doorsDone?.refinish || 0) + Number(snap.doorsDone?.maintenance || 0);

  return (
    <div className="bm-panel bdr-grad-container-med bdr-border-thin bdr-shadow-med">
      <div className="dm-panel-head">
        <h3 className="bm-panel-title">Breakdown</h3>
        <div className="dm-panel-head-actions">
          <div className="dm-toggle" role="tablist" aria-label="Kind">
            {["refinish", "maintenance"].map((k) => (
              <button key={k} type="button" role="tab" aria-selected={kind === k} className={`dm-toggle-btn ${kind === k ? "dm-toggle-active" : ""}`} onClick={() => setKind(k)}>
                {k === "refinish" ? "Refinish" : "Maintenance"}
              </button>
            ))}
          </div>
          <button type="button" className="dm-link" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      {open && (
        <>
          <h4 className="dm-h4">
            Materials per door — {mode === "recipe" ? "recipe" : "actual (calibrated)"} · {money(block?.materialsCents)}
            {snap.recipeVersions?.[kind] != null && <span className="bm-muted"> · recipe v{snap.recipeVersions[kind]}</span>}
          </h4>
          {lines.length === 0 ? (
            <div className="bm-empty-small">
              {mode === "recipe" ? "No recipe for this kind yet." : "Nothing issued in the window and no recipe to fall back on."}
            </div>
          ) : (
            <div className="bm-table-scroll">
              <table className="bm-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="bm-num">Qty / door</th>
                    <th className="bm-num">Cost / stock unit</th>
                    <th className="bm-num">Per door</th>
                    {mode === "actual" && (
                      <>
                        <th className="bm-num">Issued (90d)</th>
                        <th className="bm-num">Recipe predicted</th>
                        <th className="bm-num">Scale</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.itemId}>
                      <td>
                        {l.itemName}
                        {l.priceMissing && <span className="dm-flag dm-flag-warn">no price</span>}
                        {mode === "actual" && l.inRecipe === false && <span className="dm-flag">not in recipe</span>}
                        {mode === "actual" && l.inRecipe && l.inWindow === false && <span className="dm-flag">nothing issued — recipe value</span>}
                        {mode === "actual" && l.observedNegative && <span className="dm-flag dm-flag-warn">returns exceed issues</span>}
                      </td>
                      <td className="bm-num">
                        {(() => {
                          const q = parseFloat(l.qtyPerDoor);
                          const c = snap.itemContents?.[l.itemId];
                          const ppu = c ? parseFloat(c.piecesPerUnit) : NaN;
                          if (!Number.isFinite(q)) return "--";
                          if (!c || !(ppu > 0)) return fmtQty(q);
                          const pieces = fmtQty(q * ppu);
                          return (
                            <>
                              {pieces} {pluralize(c.pieceName, Number(pieces))}
                              <div className="dm-qty-hint">{fmtQty(q)} {unitNoun(c.stockUnit)}</div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="bm-num">
                        {money(l.unitCostCents)}
                        {snap.itemContents?.[l.itemId] ? ` / ${unitNoun(snap.itemContents[l.itemId].stockUnit)}` : ""}
                      </td>
                      <td className="bm-num bm-money">{money(l.lineCents)}</td>
                      {mode === "actual" && (
                        <>
                          <td className="bm-num">{num(l.observedQty)}</td>
                          <td className="bm-num">{num(l.predictedQty, 2)}</td>
                          <td className="bm-num">{l.scale == null ? "--" : `×${num(l.scale, 2)}`}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4 className="dm-h4">Overhead per door · over {num(allDoors)} doors in the window</h4>
          <div className="bm-table-scroll">
            <table className="bm-table">
              <tbody>
                <tr>
                  <td>
                    Software
                    {ovh.softwareIncomplete && <span className="dm-flag dm-flag-warn">incomplete — lower bound</span>}
                  </td>
                  <td className="bm-num bm-muted">{money(ovh.totals?.softwareCents)} in window</td>
                  <td className="bm-num bm-money">{money(ovh.softwareCents)}</td>
                </tr>
                <tr>
                  <td>
                    {ovh.marketingAppliesTo === "refinish" ? "Marketing (refinish doors only, 12-month window)" : "Marketing (blended, 12-month window)"}
                  </td>
                  <td className="bm-num bm-muted">
                    {money(ovh.totals?.marketingCents)} over{" "}
                    {ovh.marketingAppliesTo === "refinish"
                      ? `${num(ovh.marketingRefinishDoors)} refinish doors`
                      : `${num(snap.doorsDone?.marketingWindowDoors)} doors`}
                  </td>
                  <td className="bm-num bm-money">{money(ovh.marketingBlendedCents)}</td>
                </tr>
                {(ovh.marketingBySource || []).map((s) => (
                  <tr key={s.sourceOptionId} className="dm-sub-row">
                    <td>↳ {s.source}</td>
                    <td className="bm-num bm-muted">
                      {money(s.costCents)} over {num(s.nDoors)} doors
                    </td>
                    <td className="bm-num">{money(s.perDoorCents)}</td>
                  </tr>
                ))}
                <tr>
                  <td>Damage / warranty</td>
                  <td className="bm-num bm-muted">{money(ovh.totals?.damageCents)} in window</td>
                  <td className="bm-num bm-money">{money(ovh.damageCents)}</td>
                </tr>
                <tr>
                  <td>Other</td>
                  <td className="bm-num bm-muted">{money(ovh.totals?.otherCents)} in window</td>
                  <td className="bm-num bm-money">{money(ovh.otherCents)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {ovh.marketingAppliesTo === "refinish" && (
            <div className="dm-footnote">
              Maintenance doors carry no marketing — they're return customers on doors we refinished. Overhead per
              door: refinish {money(ovh.refinishCents)} · maintenance {money(ovh.maintenanceCents)}.
            </div>
          )}
          {(snap.doorsDone?.unmatchedLines?.length > 0 || snap.doorsDone?.unresolvedLedgerRows > 0) && (
            <div className="dm-footnote">
              {snap.doorsDone.unmatchedLines?.length > 0 && (
                <div>
                  Counted as 1.0 door (name not in the services catalog):{" "}
                  {snap.doorsDone.unmatchedLines.map((u) => `${u.itemName} ×${u.count}`).join(", ")}
                </div>
              )}
              {snap.doorsDone.unresolvedLedgerRows > 0 && (
                <div>{snap.doorsDone.unresolvedLedgerRows} ledger row(s) had no invoice and were skipped.</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DoorMarginBreakdown;
